class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);

    this.debouncedOnSubmit = debounce((event) => {
      this.onSubmitHandler(event);
    }, 800);

    const facetForm = this.querySelector('form');
    facetForm.addEventListener('input', this.debouncedOnSubmit.bind(this));

    const facetWrapper = this.querySelector('#FacetsWrapperDesktop');
    if (facetWrapper) facetWrapper.addEventListener('keyup', onKeyUpEscape);
  }

  static setListeners() {
    FacetFiltersForm.mobileFacetsMarkup = document.getElementById('FacetsWrapperMobile')?.innerHTML || '';
    const onHistoryChange = (event) => {
      const searchParams = event.state ? event.state.searchParams : FacetFiltersForm.searchParamsInitial;
      if (searchParams === FacetFiltersForm.searchParamsPrev) return;
      FacetFiltersForm.renderPage(searchParams, null, false);
    };
    window.addEventListener('popstate', onHistoryChange);
  }

  static toggleActiveFacets(disable = true) {
    document.querySelectorAll('.js-facet-remove').forEach((element) => {
      element.classList.toggle('disabled', disable);
    });
  }

  static renderPage(searchParams, event, updateURLHash = true) {
    FacetFiltersForm.searchParamsPrev = searchParams;
    const sections = FacetFiltersForm.getSections();
    const updateEvent = FacetFiltersForm.startUpdateEvent(searchParams);
    const countContainer = document.getElementById('ProductCount');
    const countContainerDesktop = document.getElementById('ProductCountDesktop');
    const loadingSpinners = document.querySelectorAll(
      '.facets-container .loading__spinner, facet-filters-form .loading__spinner',
    );
    loadingSpinners.forEach((spinner) => spinner.classList.remove('hidden'));
    document.getElementById('ProductGridContainer').querySelector('.collection').classList.add('loading');
    if (countContainer) {
      countContainer.classList.add('loading');
    }
    if (countContainerDesktop) {
      countContainerDesktop.classList.add('loading');
    }

    sections.forEach((section) => {
      const url = `${window.location.pathname}?section_id=${section.section}&${searchParams}`;
      const filterDataUrl = (element) => element.url === url;

      FacetFiltersForm.filterData.some(filterDataUrl)
        ? FacetFiltersForm.renderSectionFromCache(filterDataUrl, event, updateEvent)
        : FacetFiltersForm.renderSectionFromFetch(url, event, updateEvent);
    });

    if (updateURLHash) FacetFiltersForm.updateURLHash(searchParams);
  }

  static startUpdateEvent(searchParams) {
    const { SearchUpdateEvent, CollectionUpdateEvent } = window.StandardEvents || {};
    if (!SearchUpdateEvent && !CollectionUpdateEvent) return;

    const facetsForm = document.querySelector('facet-filters-form');
    const facetsContainer = document.querySelector('.facets-container');
    const collectionId = facetsContainer?.dataset.collectionId || null;
    const collectionHandle = facetsContainer?.dataset.collectionHandle || '';
    const currentCount = parseInt(document.getElementById('ProductCount')?.dataset.productCount) || 0;
    const urlSearchParams = new URLSearchParams(searchParams);
    const isSearchPage = facetsContainer?.dataset.template === 'search';
    const isCollectionPage = facetsContainer?.dataset.template === 'collection';
    const dispatchTarget = facetsForm || document;

    let deferred;

    if (isSearchPage) {
      deferred = SearchUpdateEvent.createPromise();
      dispatchTarget.dispatchEvent(
        new SearchUpdateEvent({
          search: {
            query: urlSearchParams.get('q') || '',
            productFilters: SearchUpdateEvent.parseProductFilters(urlSearchParams),
            sortKey: SearchUpdateEvent.getSortKey(urlSearchParams),
          },
          promise: deferred.promise,
        }),
      );
    } else if (isCollectionPage) {
      // collectionId is null for auto-generated collections (/collections/all,
      // /collections/vendors/<x>, etc.) — the SSE schema accepts that.
      deferred = CollectionUpdateEvent.createPromise();
      dispatchTarget.dispatchEvent(
        new CollectionUpdateEvent({
          collection: { id: collectionId, handle: collectionHandle, productsCount: currentCount },
          productFilters: CollectionUpdateEvent.parseProductFilters(urlSearchParams),
          sortKey: CollectionUpdateEvent.getSortKey(urlSearchParams),
          promise: deferred.promise,
        }),
      );
    }

    if (!deferred) return;

    return {
      resolve: (filteredCount) => {
        deferred.resolve({ [isSearchPage ? 'totalCount' : 'productsCount']: filteredCount });
      },
      reject: deferred.reject,
    };
  }

  static renderSectionFromFetch(url, event, updateEvent) {
    fetch(url)
      .then((response) => response.text())
      .then((html) => {
        FacetFiltersForm.filterData = [...FacetFiltersForm.filterData, { html, url }];
        FacetFiltersForm.renderSection(html, event, updateEvent);
      })
      .catch((error) => {
        console.error(error);
        updateEvent?.reject(error);
      });
  }

  static renderSectionFromCache(filterDataUrl, event, updateEvent) {
    const html = FacetFiltersForm.filterData.find(filterDataUrl).html;
    FacetFiltersForm.renderSection(html, event, updateEvent);
  }

  static renderSection(html, event, updateEvent) {
    FacetFiltersForm.renderFilters(html, event);
    FacetFiltersForm.renderProductGridContainer(html);
    FacetFiltersForm.renderProductCount(html, updateEvent);
    if (typeof initializeScrollAnimationTrigger === 'function') initializeScrollAnimationTrigger(html.innerHTML);
  }

  static renderProductGridContainer(html) {
    document.getElementById('ProductGridContainer').innerHTML = new DOMParser()
      .parseFromString(html, 'text/html')
      .getElementById('ProductGridContainer').innerHTML;

    document
      .getElementById('ProductGridContainer')
      .querySelectorAll('.scroll-trigger')
      .forEach((element) => {
        element.classList.add('scroll-trigger--cancel');
      });
  }

  static renderProductCount(html, updateEvent) {
    const parsedHtml = new DOMParser().parseFromString(html, 'text/html');
    const sourceCount = parsedHtml.getElementById('ProductCount');
    const count = sourceCount.innerHTML;
    const container = document.getElementById('ProductCount');
    const containerDesktop = document.getElementById('ProductCountDesktop');
    container.innerHTML = count;
    container.dataset.productCount = sourceCount.dataset.productCount || '';
    container.dataset.totalCount = sourceCount.dataset.totalCount || '';
    container.classList.remove('loading');
    if (containerDesktop) {
      containerDesktop.innerHTML = count;
      containerDesktop.classList.remove('loading');
    }
    const loadingSpinners = document.querySelectorAll(
      '.facets-container .loading__spinner, facet-filters-form .loading__spinner',
    );
    loadingSpinners.forEach((spinner) => spinner.classList.add('hidden'));

    updateEvent?.resolve(parseInt(sourceCount.dataset.productCount) || 0);
  }

  static renderFilters(html, event) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');
    const facetDetailsElementsFromFetch = parsedHTML.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter',
    );
    const facetDetailsElementsFromDom = document.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter',
    );

    // Remove facets that are no longer returned from the server
    Array.from(facetDetailsElementsFromDom).forEach((currentElement) => {
      if (!Array.from(facetDetailsElementsFromFetch).some(({ id }) => currentElement.id === id)) {
        currentElement.remove();
      }
    });

    const matchesId = (element) => {
      const jsFilter = event ? event.target.closest('.js-filter') : undefined;
      return jsFilter ? element.id === jsFilter.id : false;
    };

    const facetsToRender = Array.from(facetDetailsElementsFromFetch).filter((element) => !matchesId(element));
    const countsToRender = Array.from(facetDetailsElementsFromFetch).find(matchesId);

    facetsToRender.forEach((elementToRender, index) => {
      const currentElement = document.getElementById(elementToRender.id);
      // Element already rendered in the DOM so just update the innerHTML
      if (currentElement) {
        document.getElementById(elementToRender.id).innerHTML = elementToRender.innerHTML;
      } else {
        if (index > 0) {
          const { className: previousElementClassName, id: previousElementId } = facetsToRender[index - 1];
          // Same facet type (eg horizontal/vertical or drawer/mobile)
          if (elementToRender.className === previousElementClassName) {
            document.getElementById(previousElementId).after(elementToRender);
            return;
          }
        }

        if (elementToRender.parentElement) {
          document.querySelector(`#${elementToRender.parentElement.id} .js-filter`).before(elementToRender);
        }
      }
    });

    FacetFiltersForm.renderActiveFacets(parsedHTML);
    FacetFiltersForm.renderAdditionalElements(parsedHTML);

    if (countsToRender) {
      const closestJSFilterID = event.target.closest('.js-filter').id;

      if (closestJSFilterID) {
        FacetFiltersForm.renderCounts(countsToRender, event.target.closest('.js-filter'));
        FacetFiltersForm.renderMobileCounts(countsToRender, document.getElementById(closestJSFilterID));

        const newFacetDetailsElement = document.getElementById(closestJSFilterID);

        const isTextInput = event.target.getAttribute('type') === 'text';

        if (!isTextInput) {
          // Try to return focus to the same checkbox the user just toggled,
          // re-selecting it from the freshly rendered HTML by its id.
          const originatingInputId = event.target.id;
          const matchingInput = originatingInputId
            ? newFacetDetailsElement.querySelector(`#${CSS.escape(originatingInputId)}`)
            : null;

          if (matchingInput) {
            matchingInput.focus();
          } else {
            // Fallback to summary/close button if the checkbox can't be found
            const fallbackSelector = newFacetDetailsElement.classList.contains('mobile-facets__details')
              ? `.mobile-facets__close-button`
              : `.facets__summary`;
            const fallbackElement = newFacetDetailsElement.querySelector(fallbackSelector);
            if (fallbackElement) fallbackElement.focus();
          }
        }
      }
    }
  }

  static renderActiveFacets(html) {
    const activeFacetElementSelectors = ['.active-facets-mobile', '.active-facets-desktop'];

    activeFacetElementSelectors.forEach((selector) => {
      const activeFacetsElement = html.querySelector(selector);
      if (!activeFacetsElement) return;
      document.querySelector(selector).innerHTML = activeFacetsElement.innerHTML;
    });

    FacetFiltersForm.toggleActiveFacets(false);
  }

  static renderAdditionalElements(html) {
    const mobileElementSelectors = ['.mobile-facets__open', '.mobile-facets__count', '.sorting'];

    mobileElementSelectors.forEach((selector) => {
      if (!html.querySelector(selector)) return;
      document.querySelector(selector).innerHTML = html.querySelector(selector).innerHTML;
    });

    const categoryNavigation = html.querySelector('.degggo-collection-categories');
    if (categoryNavigation) {
      document.querySelectorAll('.degggo-collection-categories').forEach((element) => {
        element.innerHTML = categoryNavigation.innerHTML;
      });
    }

    document.getElementById('FacetFiltersFormMobile').closest('menu-drawer').bindEvents();
  }

  static syncCollectionCategoryCheckboxes(handles) {
    const selectedHandles = new Set(handles);
    document.querySelectorAll('.degggo-collection-categories__input').forEach((input) => {
      input.checked = selectedHandles.has(input.dataset.collectionHandle);
    });
  }

  static updateCollectionCategoryCount(count) {
    const collectionComponent = document.querySelector('collection-component');
    const countTemplate = collectionComponent?.dataset.productCountTemplate || '__COUNT__';
    const countText = countTemplate.replace('__COUNT__', count);

    ['#ProductCount', '#ProductCountDesktop'].forEach((selector) => {
      const countElement = document.querySelector(selector);
      if (!countElement) return;
      countElement.textContent = countText;
      countElement.dataset.productCount = count;
      countElement.classList.remove('loading');
    });

    const mobileCount = document.querySelector('.mobile-facets__count');
    if (mobileCount) mobileCount.textContent = countText;
  }

  static renderCollectionCategoryResults(handles, searchParams) {
    const productGridContainer = document.getElementById('ProductGridContainer');
    const productGrid = document.getElementById('product-grid');
    if (!productGridContainer || !productGrid) return;

    const mobileFacetsMain = document.getElementById('FacetsWrapperMobile');
    const mobileFacetsMarkup = FacetFiltersForm.mobileFacetsMarkup;

    productGridContainer.querySelector('.collection')?.classList.add('loading');
    const sectionId = productGrid.dataset.id;
    const query = searchParams ? `&${searchParams}` : '';
    const uniqueHandles = [...new Set(handles)];
    const urls = uniqueHandles.map(
      (handle) => `${window.location.origin}/collections/${encodeURIComponent(handle)}?section_id=${encodeURIComponent(sectionId)}${query}`,
    );

    Promise.all(
      urls.map((url) =>
        fetch(url).then((response) => {
          if (!response.ok) throw new Error(`Collection request failed: ${response.status}`);
          return response.text();
        }),
      ),
    )
      .then((responses) => {
        const documents = responses.map((response) => new DOMParser().parseFromString(response, 'text/html'));
        const sourceContainers = documents
          .map((document) => document.getElementById('ProductGridContainer'))
          .filter(Boolean);
        if (!sourceContainers.length) return;

        const baseContainer =
          sourceContainers.find((container) => container.querySelector('#product-grid')) || sourceContainers[0];
        const baseGrid = baseContainer.querySelector('#product-grid');
        let combinedGrid = productGridContainer.querySelector('#product-grid');

        if (baseGrid && combinedGrid) {
          combinedGrid.className = baseGrid.className;
          combinedGrid.innerHTML = baseGrid.innerHTML;
        } else if (baseGrid) {
          productGridContainer.innerHTML = baseContainer.innerHTML;
          combinedGrid = productGridContainer.querySelector('#product-grid');
        } else {
          productGridContainer.innerHTML = baseContainer.innerHTML;
          combinedGrid = null;
        }
        let productCount = 0;
        const seenProducts = new Set();

        if (combinedGrid) {
          const productKey = (item) => item.querySelector('a[href*="/products/"]')?.getAttribute('href') || item.innerHTML;

          Array.from(combinedGrid.children).forEach((item) => {
            seenProducts.add(productKey(item));
          });

          sourceContainers.forEach((container, sourceIndex) => {
            const sourceGrid = container.querySelector('#product-grid');
            if (!sourceGrid) return;

            Array.from(sourceGrid.children).forEach((item) => {
              const key = productKey(item);
              if (sourceIndex === 0 || seenProducts.has(key)) return;
              seenProducts.add(key);
              combinedGrid.appendChild(document.importNode(item, true));
            });
          });

          productGridContainer.querySelectorAll('.pagination').forEach((pagination) => pagination.remove());
          productCount = combinedGrid.children.length;
        }

        FacetFiltersForm.syncCollectionCategoryCheckboxes(uniqueHandles);
        FacetFiltersForm.updateCollectionCategoryCount(productCount);
      })
      .catch((error) => console.error(error))
      .finally(() => {
        if (mobileFacetsMain && !mobileFacetsMain.children.length && mobileFacetsMarkup) {
          mobileFacetsMain.innerHTML = mobileFacetsMarkup;
        }
        FacetFiltersForm.syncCollectionCategoryCheckboxes(uniqueHandles);
        document.getElementById('FacetFiltersFormMobile')?.closest('menu-drawer')?.bindEvents();
        productGridContainer.querySelector('.collection')?.classList.remove('loading');
      });
  }

  static renderCounts(source, target) {
    const targetSummary = target.querySelector('.facets__summary');
    const sourceSummary = source.querySelector('.facets__summary');

    if (sourceSummary && targetSummary) {
      targetSummary.outerHTML = sourceSummary.outerHTML;
    }

    const targetHeaderElement = target.querySelector('.facets__header');
    const sourceHeaderElement = source.querySelector('.facets__header');

    if (sourceHeaderElement && targetHeaderElement) {
      targetHeaderElement.outerHTML = sourceHeaderElement.outerHTML;
    }

    const targetWrapElement = target.querySelector('.facets-wrap');
    const sourceWrapElement = source.querySelector('.facets-wrap');

    if (sourceWrapElement && targetWrapElement) {
      const isShowingMore = Boolean(target.querySelector('show-more-button .label-show-more.hidden'));
      if (isShowingMore) {
        sourceWrapElement
          .querySelectorAll('.facets__item.hidden')
          .forEach((hiddenItem) => hiddenItem.classList.replace('hidden', 'show-more-item'));
      }

      targetWrapElement.outerHTML = sourceWrapElement.outerHTML;
    }
  }

  static renderMobileCounts(source, target) {
    const targetFacetsList = target.querySelector('.mobile-facets__list');
    const sourceFacetsList = source.querySelector('.mobile-facets__list');

    if (sourceFacetsList && targetFacetsList) {
      targetFacetsList.outerHTML = sourceFacetsList.outerHTML;
    }
  }

  static updateURLHash(searchParams) {
    history.pushState({ searchParams }, '', `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`);
  }

  static getSections() {
    return [
      {
        section: document.getElementById('product-grid').dataset.id,
      },
    ];
  }

  createSearchParams(form, removeCollectionCategories = false) {
    const formData = new FormData(form);
    if (removeCollectionCategories) {
      form.querySelectorAll('.degggo-collection-categories__input').forEach((input) => {
        formData.delete(input.name);
      });
    }
    form.querySelectorAll('.degggo-price-range__input').forEach((input) => {
      if (Number(input.value) >= Number(input.dataset.max)) {
        formData.delete(input.name);
      }
    });
    return new URLSearchParams(formData).toString();
  }

  onSubmitForm(searchParams, event) {
    FacetFiltersForm.renderPage(searchParams, event);
  }

  getSelectedCollectionHandles(form) {
    const formInputs = form?.querySelectorAll('.degggo-collection-categories__input') || [];
    const inputs = formInputs.length
      ? Array.from(formInputs)
      : Array.from(document.querySelectorAll('.degggo-collection-categories__input'));
    return [
      ...new Set(
        inputs
          .filter((input) => input.checked && input.dataset.collectionHandle)
          .map((input) => input.dataset.collectionHandle),
      ),
    ];
  }

  getCombinedSearchParamsForCollectionFilter(form) {
    if (form?.id === 'FacetFiltersFormMobile') {
      return this.createSearchParams(form, true);
    }

    const forms = [];
    document.querySelectorAll('facet-filters-form form').forEach((currentForm) => {
      if (currentForm.id === 'FacetSortForm' || currentForm.id === 'FacetFiltersForm' || currentForm.id === 'FacetSortDrawerForm') {
        forms.push(this.createSearchParams(currentForm, true));
      }
    });
    return forms.join('&');
  }

  onSubmitHandler(event) {
    event.preventDefault();
    const sortFilterForms = document.querySelectorAll('facet-filters-form form');
    const form = event.target.closest('form');
    const isCollectionCategoryInput = event.target.matches?.('.degggo-collection-categories__input') === true;
    const selectedCollectionHandles = this.getSelectedCollectionHandles(form);

    if (isCollectionCategoryInput || selectedCollectionHandles.length > 0) {
      const searchParams = this.getCombinedSearchParamsForCollectionFilter(form);
      if (selectedCollectionHandles.length > 0) {
        FacetFiltersForm.renderCollectionCategoryResults(selectedCollectionHandles, searchParams);
      } else {
        this.onSubmitForm(searchParams, event);
      }
      return;
    }

    const isCheckboxFilter = event.target instanceof HTMLInputElement && event.target.type === 'checkbox';

    if (isCheckboxFilter) {
      const searchParams = this.createSearchParams(event.target.closest('form'));
      this.onSubmitForm(searchParams, event);
    } else {
      const forms = [];
      const isMobile = event.target.closest('form').id === 'FacetFiltersFormMobile';

      sortFilterForms.forEach((form) => {
        if (!isMobile) {
          if (form.id === 'FacetSortForm' || form.id === 'FacetFiltersForm' || form.id === 'FacetSortDrawerForm') {
            forms.push(this.createSearchParams(form));
          }
        } else if (form.id === 'FacetFiltersFormMobile') {
          forms.push(this.createSearchParams(form));
        }
      });
      this.onSubmitForm(forms.join('&'), event);
    }
  }

  onActiveFilterClick(event) {
    event.preventDefault();
    FacetFiltersForm.toggleActiveFacets();
    const url =
      event.currentTarget.href.indexOf('?') == -1
        ? ''
        : event.currentTarget.href.slice(event.currentTarget.href.indexOf('?') + 1);
    FacetFiltersForm.renderPage(url);
  }
}

FacetFiltersForm.filterData = [];
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);
customElements.define('facet-filters-form', FacetFiltersForm);
FacetFiltersForm.setListeners();

class PriceRange extends HTMLElement {
  constructor() {
    super();
    this.querySelectorAll('input').forEach((element) => {
      element.addEventListener('input', this.onRangeInput.bind(this));
      element.addEventListener('change', this.onRangeChange.bind(this));
      element.addEventListener('keydown', this.onKeyDown.bind(this));
    });
    this.setMinAndMaxValues();
    this.updateSliderDisplay();
  }

  onRangeInput(event) {
    this.adjustToValidValues(event.currentTarget);
    this.updateSliderDisplay();
  }

  onRangeChange(event) {
    this.adjustToValidValues(event.currentTarget);
    this.setMinAndMaxValues();
    this.updateSliderDisplay();
  }

  onKeyDown(event) {
    if (event.metaKey) return;

    const pattern = /[0-9]|\.|,|'| |Tab|Backspace|Enter|ArrowUp|ArrowDown|ArrowLeft|ArrowRight|Delete|Escape/;
    if (!event.key.match(pattern)) event.preventDefault();
  }

  setMinAndMaxValues() {
    const inputs = this.querySelectorAll('input');
    const minInput = inputs[0];
    const maxInput = inputs[1];
    if (maxInput.value) minInput.setAttribute('data-max', maxInput.value);
    if (minInput.value) maxInput.setAttribute('data-min', minInput.value);
    if (minInput.value === '') maxInput.setAttribute('data-min', 0);
    if (maxInput.value === '') minInput.setAttribute('data-max', maxInput.getAttribute('data-max'));
  }

  updateSliderDisplay() {
    const rangeInput = this.querySelector('.degggo-price-range__input');
    if (!rangeInput) return;

    const min = Number(rangeInput.min || 0);
    const max = Number(rangeInput.max || rangeInput.dataset.max || 1);
    const value = Number(rangeInput.value || max);
    const progress = max > min ? ((value - min) / (max - min)) * 100 : 100;
    const display = this.querySelector('[data-price-range-value]');

    this.style.setProperty('--degggo-price-progress', `${Math.max(0, Math.min(100, progress))}%`);
    if (display) display.textContent = Math.round(value).toLocaleString('sr-RS');
  }

  adjustToValidValues(input) {
    const value = Number(input.value);
    const min = Number(input.getAttribute('data-min'));
    const max = Number(input.getAttribute('data-max'));

    if (value < min) input.value = min;
    if (value > max) input.value = max;
  }
}

customElements.define('price-range', PriceRange);

class FacetRemove extends HTMLElement {
  constructor() {
    super();
    const facetLink = this.querySelector('a');
    facetLink.setAttribute('role', 'button');
    facetLink.addEventListener('click', this.closeFilter.bind(this));
    facetLink.addEventListener('keyup', (event) => {
      event.preventDefault();
      if (event.code.toUpperCase() === 'SPACE') this.closeFilter(event);
    });
  }

  closeFilter(event) {
    event.preventDefault();
    const form = this.closest('facet-filters-form') || document.querySelector('facet-filters-form');
    form.onActiveFilterClick(event);
  }
}

customElements.define('facet-remove', FacetRemove);
